import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { ArrowUpRight, ChevronDown } from "assets/icons";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  Checkbox,
  Chip,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from "@heroui/react";
import {
  Archive,
  Building,
  Buildings,
  ChartBar,
  Eye,
  ListBullets,
  MagnifyingGlass,
  User,
  Envelope,
  ChatCircle,
  X,
  PencilSimple,
} from "@phosphor-icons/react";
import { useSession, useUser } from "@clerk/clerk-react";
import { InsuranceIssue } from "shared/types/insurance";
import { PolicyMetrics as PolicyPolicyMetrics } from "shared/types/insurance-policy";
import { Link } from "react-router-dom";
// import { applyMask } from "shared/utils/masks";
import { EmailModal } from "screens/helpdesk/components/emailModal";
import { QuickNotesModal } from "screens/helpdesk/components/quickNotesModal";
import { toast } from "react-toastify";
import { Kaylee } from "assets/icons";
import QuickAddInsuranceIssueModal from "screens/buildings/components/insurances/QuickAddInsuranceIssueModal";
import QuickEditInsuranceModal from "screens/buildings/components/insurances/QuickEditInsuranceModal";
import PolicyOverview from "screens/buildings/components/insurances/policyOverview";


const InsuranceFiles = () => {
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [allInsuranceFiles, setAllInsuranceFiles] = useState<InsuranceIssue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { session } = useSession();
  const { user } = useUser();
  const [selectedIssueForEmails, setSelectedIssueForEmails] = useState<InsuranceIssue | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedIssueForNotes, setSelectedIssueForNotes] = useState<InsuranceIssue | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [quickAddModalOpen, setQuickAddModalOpen] = useState(false);
  const [selectedIssueForEdit, setSelectedIssueForEdit] = useState<InsuranceIssue | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [metrics, setMetrics] = useState<{ runningFiles: number; archivedFiles: number; totalFiles: number } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  // Removed unused policyList state
  const [policyMetrics, setPolicyMetrics] = useState<PolicyPolicyMetrics | null>(null);

  const fetchControllerRef = useRef<AbortController | null>(null);
  const lastFetchParamsRef = useRef<string>("");

  // Client-side filtering with useMemo for performance
  const filteredInsuranceFiles = useMemo(() => {
    let filtered = allInsuranceFiles;

    // Filter by archived status
    if (!showArchived) {
      filtered = filtered.filter(file => !file.archived);
    }

    // Client-side search filtering for instant results
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(file => 
        (file.building?.id && file.building.id.toLowerCase().includes(term)) ||
        ((file.building as any)?.googlePlaceLongAddress && 
         (file.building as any).googlePlaceLongAddress.toLowerCase().includes(term)) ||
        file.title.toLowerCase().includes(term) ||
        file.description?.toLowerCase().includes(term)
      );
    }

    // Building filter
    if (buildingId && buildingId !== "All") {
      filtered = filtered.filter(file => file.building?.id === buildingId);
    }

    return filtered;
  }, [allInsuranceFiles, showArchived, searchTerm, buildingId]);

  // Debounce search term for server requests
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }
    
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, 500); // Increased to 500ms for less server requests

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fetchControllerRef.current && !fetchControllerRef.current.signal.aborted) {
        fetchControllerRef.current.abort();
      }
    };
  }, []);

  const fetchInsuranceFiles = useCallback(async (isInitialLoad = false) => {
    if (!session) return;
    
    try {
      // Cancel previous request if it's still in progress
      if (fetchControllerRef.current && !fetchControllerRef.current.signal.aborted) {
        fetchControllerRef.current.abort();
      }

      // Create new AbortController for this request
      fetchControllerRef.current = new AbortController();
      const currentController = fetchControllerRef.current; // Store reference to avoid race conditions

      // Build params string for caching
      const params = new URLSearchParams();
      if (showArchived) {
        params.append('includeArchived', 'true');
      }
      if (debouncedSearchTerm.trim() && debouncedSearchTerm.length > 2) {
        params.append('searchTerm', debouncedSearchTerm.trim());
      }
      
      const paramsString = params.toString();
      
      // Skip if same request as last one
      if (paramsString === lastFetchParamsRef.current && !isInitialLoad) {
        return;
      }
      
      lastFetchParamsRef.current = paramsString;

      // Only show main loading spinner on initial load
      if (isInitialLoad) {
        setIsLoading(true);
      }
      
      const response = await fetchWithAuth(
        `${API_URL}/insurance/files-with-email-counts?${paramsString}`,
        { 
          method: "GET",
          signal: currentController.signal 
        },
        session,
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Only update state if request wasn't aborted
      if (!currentController.signal.aborted) {
        setAllInsuranceFiles(data);
        if (isInitialLoad) {
          setIsInitialLoading(false);
        }
      }
    } catch (error: any) {
      // Ignore abort errors as they are expected when cancelling requests
      if (error.name !== 'AbortError') {
        if (isInitialLoad) {
          console.error("Failed to fetch insurance files:", error);
          toast.error("Fout bij laden van verzekeringsdossiers");
        }
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
      // Only clear if this is still the current controller
      if (fetchControllerRef.current?.signal.aborted === false) {
        fetchControllerRef.current = null;
      }
    }
  }, [session, showArchived, debouncedSearchTerm]);

  const fetchMetrics = useCallback(async () => {
    if (!session) return;
    
    try {
      const response = await fetchWithAuth(
        `${API_URL}/insurance/metrics`,
        { method: "GET" },
        session,
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setMetrics(data);
    } catch (error) {
      // Only log metrics errors on initial load to avoid cluttering logs
    }
  }, [session]);

  // Initial data load
  useEffect(() => {
    if (session) {
      fetchInsuranceFiles(true);
      fetchMetrics();
    }
  }, [session, fetchInsuranceFiles, fetchMetrics]);

  // Fetch policies and their metrics for dossiers KPIs
  useEffect(() => {
    if (!session) return;
    (async () => {
      try {
        const polMetRes = await fetchWithAuth(`${API_URL}/insurance/policies/metrics`, { method: "GET" }, session);
        if (polMetRes.ok) {
          const pMetrics = await polMetRes.json();
          setPolicyMetrics(pMetrics);
        }
      } catch {}
    })();
  }, [session]);

  // Server-side search for more comprehensive results (less frequent)
  useEffect(() => {
    if (session && debouncedSearchTerm.length > 2) { // Only search server if 3+ chars
      fetchInsuranceFiles(false);
    }
  }, [debouncedSearchTerm, session, fetchInsuranceFiles]);

  // Archived toggle - refetch data
  useEffect(() => {
    if (session && !isInitialLoading) {
      fetchInsuranceFiles(false);
    }
  }, [showArchived, session, isInitialLoading, fetchInsuranceFiles]);

  const handleOpenEmails = useCallback((file: any) => {
    setSelectedIssueForEmails(file);
    setEmailModalOpen(true);
  }, []);

  const handleEmailsRead = useCallback(async () => {
    if (!selectedIssueForEmails || !session) return;
    
    try {
      await fetchWithAuth(
        `${API_URL}/insurance/file/${selectedIssueForEmails.id}/emails/mark-read`, 
        { method: 'PUT' }, 
        session
      );
      fetchInsuranceFiles(false);
    } catch (error) {
      toast.error("Fout bij markeren emails als gelezen");
    }
  }, [selectedIssueForEmails, session, fetchInsuranceFiles]);

  const handleOpenNotes = useCallback((file: InsuranceIssue) => {
    setSelectedIssueForNotes(file);
    setNotesModalOpen(true);
  }, []);

  const handleArchiveFile = useCallback(async (file: InsuranceIssue) => {
    if (!file.id || !session || !user?.id) return;
    const confirmed = window.confirm(`Dossier INS${file.id} archiveren?`);
    if (!confirmed) return;

    try {
      await fetchWithAuth(
        `${API_URL}/insurance/file/${file.id}/archive`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: user.id }),
        },
        session,
      );
      toast.success("Verzekeringsdossier gearchiveerd!");
      setAllInsuranceFiles(prev => prev.map(f => f.id === file.id ? { ...f, archived: true } : f));
      fetchMetrics();
    } catch (error) {
      toast.error("Fout bij archiveren van verzekeringsdossier");
      fetchInsuranceFiles(false);
    }
  }, [session, user?.id, fetchMetrics, fetchInsuranceFiles]);

  const handleBuildingFilterChange = useCallback((building: string) => {
    setBuildingId(building);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  const handleToggleArchived = useCallback((value: boolean) => {
    setShowArchived(value);
  }, []);

  const handleOpenEdit = useCallback((file: InsuranceIssue) => {
    setSelectedIssueForEdit(file);
    setEditModalOpen(true);
  }, []);

  // Memoized unique buildings list
  const uniqueBuildings = useMemo(() => {
    const buildingIds = allInsuranceFiles
      .map((file) => file.building?.id)
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
    return buildingIds;
  }, [allInsuranceFiles]);

  // Loading skeleton component
  const LoadingSkeleton = useMemo(() => (
    <div className="flex flex-col divide-y divide-neutral-primary/10 rounded-lg bg-white shadow-sm">
      {/* Ella Loading Message */}
      <div className="p-6 bg-linear-to-r from-moss-primary/5 to-brick-primary/5 border-l-4 border-moss-primary">
        <div className="flex items-center gap-3">
          <div className="shrink-0">
            <div className="w-10 h-10 rounded-full bg-linear-to-r from-moss-primary to-brick-primary flex items-center justify-center">
              <Kaylee />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-neutral-primary">Ella</span>
              <div className="flex gap-1">
                <div className="w-1 h-1 bg-moss-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-1 h-1 bg-moss-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-1 h-1 bg-moss-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
            <p className="text-sm text-neutral-secondary-mid mt-1">
              Ik ben de lopende verzekeringsdossiers uit de database aan het ophalen...
            </p>
          </div>
        </div>
      </div>
      
      {/* Skeleton Content */}
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-6 w-48" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-72" />
          <Skeleton className="h-8 w-32" />
        </div>
      </div>
      <div className="p-0">
        <div className="divide-y divide-neutral-primary/10">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center p-4 gap-4">
              <div className="flex items-center gap-2 min-w-[120px]">
                <Skeleton className="h-6 w-6 rounded-sm" />
                <Skeleton className="h-6 w-6 rounded-sm" />
                <Skeleton className="h-6 w-6 rounded-sm" />
              </div>
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-48 flex-1" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      </div>
    </div>
  ), []);

  // Empty state component
  const EmptyState = useMemo(() => (
    <div className="flex flex-col divide-y divide-neutral-primary/10 rounded-lg bg-white shadow-sm">
      <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
        <div className="rounded-full bg-neutral-primary/5 p-4 mb-4">
          <Archive className="h-12 w-12 text-neutral-primary/40" />
        </div>
        <h3 className="text-lg font-medium text-neutral-primary mb-2">
          {searchTerm ? "Geen resultaten gevonden" : "Geen verzekeringsdossiers"}
        </h3>
        <p className="text-neutral-secondary-mid max-w-md">
          {searchTerm 
            ? `Er zijn geen dossiers gevonden die overeenkomen met "${searchTerm}"`
            : showArchived 
              ? "Er zijn nog geen gearchiveerde verzekeringsdossiers."
              : "Er zijn nog geen actieve verzekeringsdossiers."
          }
        </p>
        {searchTerm && (
          <button
            onClick={handleClearSearch}
            className="mt-4 text-moss-primary hover:text-moss-primary/80 font-medium transition-colors"
          >
            Wis zoekopdracht
          </button>
        )}
      </div>
    </div>
  ), [searchTerm, showArchived, handleClearSearch]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Verzekeringen</title>
        </Helmet>
      </HelmetProvider>
      <div className="z-10 mb-8 grid grid-cols-1 gap-8">
        <div className="flex flex-col gap-2">
          <header className="flex items-center justify-between gap-1.5 text-lg font-medium">
            <div className="flex items-center gap-1 text-lg font-medium">
              <ListBullets weight="duotone" size={20} />
              Verzekeringen
            </div>
            <div className="flex items-center gap-4">
              <a
                className="flex items-center gap-1 rounded-full border border-neutral-primary/20 px-3 py-1.5 text-sm font-medium text-moss-primary"
                href="http://mybroker.be"
                target="_blank"
              >
                MyBroker
                <ArrowUpRight className="h-[18px] w-[18px]" />
              </a>
            </div>
          </header>

          {/* Unified Metrics - Show only once at the top */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-neutral-primary mb-4 flex items-center gap-2">
              <ChartBar className="h-5 w-5 text-moss-primary" />
              Verzekeringen metrics
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Policy Metrics */}
              {policyMetrics && (
                <>
                  <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-moss-primary" />
                      <span className="text-sm font-medium text-neutral-secondary-mid">Totaal polissen</span>
                    </div>
                    <p className="text-2xl font-bold text-neutral-primary mt-1">{policyMetrics.totalPolicies}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-status-good" />
                      <span className="text-sm font-medium text-neutral-secondary-mid">Actieve polissen</span>
                    </div>
                    <p className="text-2xl font-bold text-status-good mt-1">{policyMetrics.activePolicies}</p>
                  </div>
                  {policyMetrics.buildingsWithoutPolicies > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-red-200 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Building className="h-5 w-5 text-red-500" />
                        <span className="text-sm font-medium text-red-600">Ongedekte gebouwen</span>
                      </div>
                      <p className="text-2xl font-bold text-red-500 mt-1">{policyMetrics.buildingsWithoutPolicies}</p>
                    </div>
                  )}
                  {policyMetrics.expiringPolicies > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-yellow-200 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Building className="h-5 w-5 text-yellow-500" />
                        <span className="text-sm font-medium text-neutral-secondary-mid">Verlopende polissen</span>
                      </div>
                      <p className="text-2xl font-bold text-yellow-500 mt-1">{policyMetrics.expiringPolicies}</p>
                    </div>
                  )}
                </>
              )}
              
              {/* Issues Metrics */}
              {metrics && (
                <>
                  <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
                    <div className="flex items-center gap-2">
                      <ChartBar className="h-5 w-5 text-moss-primary" />
                      <span className="text-sm font-medium text-neutral-secondary-mid">Lopende dossiers</span>
                    </div>
                    <p className="text-2xl font-bold text-neutral-primary mt-1">{metrics.runningFiles || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Archive className="h-5 w-5 text-neutral-secondary-mid" />
                      <span className="text-sm font-medium text-neutral-secondary-mid">Gearchiveerde dossiers</span>
                    </div>
                    <p className="text-2xl font-bold text-neutral-secondary-mid mt-1">{metrics.archivedFiles || 0}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Policy Overview - without metrics */}
          <PolicyOverview hideMetrics={true} />

          {/* Issues */}
          <div className="mt-8">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <ListBullets weight="duotone" size={20} className="text-moss-primary" />
                <h2 className="text-xl font-semibold text-neutral-primary">Verzekeringen issues</h2>
              </div>
              <button
                onClick={() => setQuickAddModalOpen(true)}
                className="rounded-full bg-moss-primary text-white px-3 py-1.5 text-sm font-medium hover:bg-moss-primary/90 shadow-sm"
              >
                Snel dossier toevoegen
              </button>
            </div>


            <div className="flex flex-col divide-y divide-neutral-primary/10 rounded-lg bg-white shadow-sm">
            <div className="sticky top-20 z-20 flex flex-col gap-4 rounded-tl-lg rounded-tr-lg bg-white/80 p-4 shadow-sm backdrop-blur-md">
              <div className="flex items-center justify-between">
                <b className="text-sm">Zoeken & Filters:</b>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <Input
                  placeholder="Zoek op gebouw ID of adres..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  startContent={
                    isSearching ? (
                      <Spinner size="sm" className="h-4 w-4" />
                    ) : (
                      <MagnifyingGlass className="h-4 w-4 text-neutral-secondary-mid" />
                    )
                  }
                  endContent={
                    searchTerm && (
                      <button
                        onClick={handleClearSearch}
                        className="text-neutral-secondary-mid hover:text-neutral-primary transition-colors"
                        type="button"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )
                  }
                  classNames={{
                    base: "w-72",
                    input: "text-sm",
                    inputWrapper: "bg-white border border-neutral-primary/20 data-[hover=true]:border-moss-primary/40 group-data-[focus=true]:border-moss-primary"
                  }}
                />
                <Switch
                  isSelected={showArchived}
                  onValueChange={handleToggleArchived}
                  size="sm"
                  color="warning"
                  startContent={<Archive className="h-4 w-4" />}
                  classNames={{
                    base: "inline-flex flex-row-reverse w-auto max-w-md bg-white border border-neutral-primary/20 rounded-lg px-2 py-1",
                    wrapper: "p-0 h-4 overflow-visible",
                    thumb: "w-6 h-6 border-2 shadow-lg",
                    label: "text-sm text-neutral-primary font-medium mr-2"
                  }}
                >
                  Toon gearchiveerd
                </Switch>
                <div className="flex items-center rounded-full border border-neutral-primary/20 px-2.5 py-1 text-sm font-medium">
                  <Popover placement="bottom" offset={10}>
                    <PopoverTrigger className="focus:outline-hidden">
                      <a className="flex cursor-pointer items-center gap-1 text-moss-primary">
                        <Buildings size={18} /> Selecteer gebouw
                        <ChevronDown className="h-[18px] w-[18px]" />
                      </a>
                    </PopoverTrigger>
                    <PopoverContent>
                      <ul className="flex flex-col gap-2 px-1 py-2">
                        <li>
                          <Checkbox
                            className="text-sm"
                            isSelected={buildingId === "All"}
                            onChange={() => handleBuildingFilterChange("All")}
                          >
                            All
                          </Checkbox>
                        </li>
                        {uniqueBuildings.map((building) => (
                          <li key={building}>
                            <Checkbox
                              className="text-sm"
                              isSelected={buildingId === building}
                              onChange={() =>
                                handleBuildingFilterChange(building as string)
                              }
                            >
                              {building}
                            </Checkbox>
                          </li>
                        ))}
                      </ul>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
            {(isInitialLoading || isLoading) ? (
              LoadingSkeleton
            ) : filteredInsuranceFiles.length === 0 ? (
              EmptyState
            ) : (
              <div className="w-full overflow-scroll">
                <Table
                  aria-label="Insurance Files Table"
                  classNames={{
                    wrapper: "shadow-none rounded-none p-0",
                    thead: "h-fit border-b-2 border-neutral-primary/40",
                    th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
                    tbody: "border-b divide-y divide-neutral-primary/15",
                    tr: "divide-x divide-neutral-primary/15",
                    td: "py-1 sm:py-2.5",
                  }}
                >
                  <TableHeader>
                    <TableColumn>Acties</TableColumn>
                    <TableColumn>ID</TableColumn>
                    <TableColumn>Titel</TableColumn>
                    <TableColumn>Status</TableColumn>
                    <TableColumn>Gebouw</TableColumn>
                    <TableColumn>Unit</TableColumn>
                    <TableColumn>Helpdesk</TableColumn>
                    <TableColumn>Status</TableColumn>
                    <TableColumn>Onze positie</TableColumn>
                    <TableColumn>Beschrijving schadegeval</TableColumn>
                    <TableColumn>Genomen acties</TableColumn>
                    <TableColumn>Complexiteiten</TableColumn>
                    <TableColumn>Bedrag gevraagd</TableColumn>
                    <TableColumn>Bedrag bevestigd</TableColumn>
                    <TableColumn>Contact onze verzekering</TableColumn>
                    <TableColumn>Contact tegenpartij</TableColumn>
                    <TableColumn>Contact verzekering tegenpartij</TableColumn>
                    <TableColumn>Laatste update</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {filteredInsuranceFiles.map((file: InsuranceIssue) => (
                        <TableRow key={file.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Link to={`/insurance/detail/${file.id}`} className="flex items-center gap-1">
                                <Tooltip content="Bekijk detail">
                                  <Eye weight="duotone" size={18} className="text-moss-primary" />
                                </Tooltip>
                              </Link>
                              <button
                                onClick={() => handleOpenEmails(file)}
                                className={`relative rounded px-2 py-1 transition-all text-xs font-medium ${
                                  (file as any).hasUnreadEmails
                                    ? 'bg-red-500/20 hover:bg-red-500 text-red-600 hover:text-white ring-1 ring-red-300'
                                    : 'bg-blue-500/10 hover:bg-blue-500 text-blue-500 hover:text-white'
                                }`}
                                title={`Emails: ${(file as any).emailCount || 0}${(file as any).hasUnreadEmails ? ` (${(file as any).unreadEmailCount} ongelezen)` : ''}`}
                              >
                                <div className="flex items-center gap-1">
                                  <Envelope className="h-4 w-4" />
                                  <span className="font-semibold">{(file as any).emailCount || 0}</span>
                                </div>
                                {(file as any).hasUnreadEmails && (
                                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                                )}
                              </button>
                              <button
                                onClick={() => handleOpenNotes(file)}
                                className="bg-moss-primary/10 hover:bg-moss-primary text-moss-primary hover:text-white rounded-sm px-2 py-1 transition-all text-xs font-medium"
                                title="Snelle notitie"
                              >
                                <ChatCircle className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleOpenEdit(file)}
                                className="bg-neutral-primary/10 hover:bg-neutral-primary text-neutral-primary hover:text-white rounded-sm px-2 py-1 transition-all text-xs font-medium"
                                title="Bewerk snel"
                              >
                                <PencilSimple className="h-4 w-4" />
                              </button>
                              {!file.archived && (
                                <button
                                  onClick={() => handleArchiveFile(file)}
                                  className="bg-neutral-primary/10 hover:bg-neutral-primary text-neutral-primary hover:text-white rounded-sm px-2 py-1 transition-all text-xs font-medium"
                                  title="Archiveer dossier"
                                >
                                  <Archive className="h-4 w-4" />
                                </button>
                              )}
                              {file.archived && (
                                <Chip
                                  size="sm"
                                  variant="flat"
                                  classNames={{
                                    base: "bg-neutral-primary/20 text-neutral-primary",
                                    content: "px-1 text-xs font-medium"
                                  }}
                                >
                                  Gearchiveerd
                                </Chip>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>INS{file.id}</TableCell>
                          <TableCell>{file.title}</TableCell>
                          <TableCell>{file.insuranceIssueStatus}</TableCell>
                          <TableCell>
                            <Chip
                              className="gap-1"
                              startContent={
                                <Building weight="duotone" size={14} />
                              }
                              classNames={{
                                base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                                content: "px-0 font-medium",
                              }}
                            >
                              {file.building?.id}
                            </Chip>
                          </TableCell>
                          <TableCell>{file.unit?.id}</TableCell>
                          <TableCell>
                            {file.helpdeskTickets && file.helpdeskTickets.length > 0 ? (
                              <Link to={`/helpdesk?highlightTicket=${file.helpdeskTickets[0].id}`} className="text-moss-primary hover:underline">
                                H{file.helpdeskTickets[0].id}
                              </Link>
                            ) : (
                              <span className="text-neutral-secondary-mid">N/A</span>
                            )}
                          </TableCell>
                          <TableCell>{file.insuranceIssueStatus}</TableCell>
                          <TableCell>{file.role}</TableCell>
                          <TableCell>
                            <div>
                              {file.description}
                            </div>
                          </TableCell>
                          <TableCell>{file.actionsTaken}</TableCell>
                          <TableCell>{file.complexities}</TableCell>
                          <TableCell>{file.askedCompensation}</TableCell>
                          <TableCell>{file.receivedCompensation}</TableCell>
                          <TableCell>
                            {file.insuranceCompanyYally ? (
                              <Link
                                to={`/${file.insuranceCompanyYally?.type}/${file.insuranceCompanyYally?.resourceId}`}
                                className="flex items-center gap-1"
                              >
                                <Chip
                                  className="gap-1"
                                  startContent={
                                    <User weight="duotone" size={14} />
                                  }
                                  classNames={{
                                    base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                                    content: "px-0 font-medium",
                                  }}
                                >
                                  {file.insuranceCompanyYally?.name}
                                </Chip>
                              </Link>
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                          <TableCell>
                            {file.counterparty ? (
                              <Link
                                to={`/${file.counterparty?.type}/${file.counterparty?.resourceId}`}
                                className="flex items-center gap-1"
                              >
                                <Chip
                                  className="gap-1"
                                  startContent={
                                    <User weight="duotone" size={14} />
                                  }
                                  classNames={{
                                    base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                                    content: "px-0 font-medium",
                                  }}
                                >
                                  {file.counterparty?.name}
                                </Chip>
                              </Link>
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                          <TableCell>
                            {file.insuranceCompanyCounterparty ? (
                              <Link
                                to={`/${file.insuranceCompanyCounterparty?.type}/${file.insuranceCompanyCounterparty?.resourceId}`}
                                className="flex items-center gap-1"
                              >
                                <Chip
                                  className="gap-1"
                                  startContent={
                                    <User weight="duotone" size={14} />
                                  }
                                  classNames={{
                                    base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                                    content: "px-0 font-medium",
                                  }}
                                >
                                  {file.insuranceCompanyCounterparty?.name}
                                </Chip>
                              </Link>
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                          <TableCell>
                            {file.updatedAt
                              ? new Date(file.updatedAt).toLocaleDateString()
                              : file.createdAt
                                ? new Date(file.createdAt).toLocaleDateString()
                                : ""}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {selectedIssueForEmails && (
        <EmailModal
          isOpen={emailModalOpen}
          onClose={() => { setEmailModalOpen(false); setSelectedIssueForEmails(null); }}
          ticket={{ id: (selectedIssueForEmails as any).id, title: (selectedIssueForEmails as any).title } as any}
          onEmailsRead={handleEmailsRead}
          isInsurance
          insuranceIssueId={(selectedIssueForEmails as any).id}
          insuranceContacts={(selectedIssueForEmails as any).insuranceCompanyYallyContacts || []}
          onUpdateInsuranceContacts={(contacts) => {
            // reflect changes locally in list so badge counts can use updated state later if needed
            setSelectedIssueForEmails((prev) => prev ? ({ ...prev, insuranceCompanyYallyContacts: contacts } as any) : prev);
          }}
        />
      )}

      {/* Notes Modal */}
      {selectedIssueForNotes && (
        <QuickNotesModal
          isOpen={notesModalOpen}
          onClose={() => { setNotesModalOpen(false); setSelectedIssueForNotes(null); }}
          insuranceIssueId={(selectedIssueForNotes as any).id}
        />
      )}

      {/* Quick Add Insurance Issue Modal */}
      <QuickAddInsuranceIssueModal
        isOpen={quickAddModalOpen}
        onClose={() => setQuickAddModalOpen(false)}
        onCreated={() => {
          setQuickAddModalOpen(false);
          fetchInsuranceFiles(true);
          fetchMetrics();
        }}
      />

      {/* Quick Edit Insurance Modal */}
      <QuickEditInsuranceModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedIssueForEdit(null);
        }}
        issue={selectedIssueForEdit}
        onUpdated={(updatedIssue) => {
          // Update the insurance file in the list
          setAllInsuranceFiles(prev => 
            prev.map(f => f.id === updatedIssue.id ? { ...f, ...updatedIssue } : f)
          );
          setEditModalOpen(false);
          setSelectedIssueForEdit(null);
        }}
      />
    </>
  );
};

export default InsuranceFiles;

// Modals rendered at root (outside table) to mirror helpdesk behavior
// Note: place these at the end of JSX return if needed. For now, we can mount them alongside this component usage.
